import type { ReactNode } from "react";
import { useNavigate } from "react-router";
import { ClinicSwitcher } from "@/design/ClinicSwitcher";
import { ADMIN_NAV, BUSINESS_NAV } from "@/design/navItems";
import type { NavItem } from "@/design/nav";
import { ThemeToggle } from "@/design/ThemeToggle";
import { switchStateFor } from "@/modules/identity/commands/activeBusinessCopy";
import { useMe } from "~/auth/MeProvider";
import { AppLink } from "~/components/AppLink";
import { AppNav } from "~/components/AppNav";
import { NotificationBell } from "~/components/Bell";
import { put } from "~/lib/api";

/**
 * The top bar for the signed-in dashboards, business and admin (R22 T14).
 *
 * The business sections are the three top-level places a clinic works:
 * Schedule (what is happening, including requests awaiting a decision),
 * Dashboard (who its customers are), and Settings (everything it configures).
 * Team, Services, availability and the public profile all live inside Settings
 * rather than competing for space in the nav.
 *
 * **The clinic switcher appears only for a member with somewhere to switch to**
 * (H37), which is almost nobody: with one clinic the chrome is exactly what it
 * was before multi-membership existed, and that is what makes the feature
 * invisible to a business that will never use it. `/me` already carries the
 * clinics, and carries them **only** when there is more than one, so the
 * single-clinic case costs no query here either.
 */
export function DashboardTopBar({
  home = "/business",
  items = BUSINESS_NAV,
  pendingCount,
}: {
  home?: string;
  items?: NavItem[];
  /** Requests awaiting a decision, badged on Schedule. */
  pendingCount?: number;
}) {
  const { me, signOut } = useMe();
  const navigate = useNavigate();

  const withBadge = pendingCount
    ? items.map((item) =>
        item.href === "/business" ? { ...item, badge: pendingCount } : item,
      )
    : items;

  const clinics = me?.kind === "member" ? me.clinics : [];

  return (
    <AppNav
      items={withBadge}
      homeHref={home}
      trailing={
        <>
          {me?.kind === "member" && clinics.length > 1 && (
            <ClinicSwitcher
              clinics={clinics}
              activeBusinessId={me.businessId}
              Link={AppLink}
              switchTo={async (businessId) => {
                const result = await put("/business/active", { businessId });
                return result.ok
                  ? { success: "switched" }
                  : switchStateFor(result.failure);
              }}
            />
          )}
          <NotificationBell />
        </>
      }
    >
      <ThemeToggle />
      <button
        type="button"
        className="text-sm font-medium text-muted hover:text-ink"
        onClick={() => {
          void signOut().then(() => navigate("/", { replace: true }));
        }}
      >
        Log out
      </button>
    </AppNav>
  );
}

/** The frame every business screen sits in. */
export function BusinessPage({
  children,
  pendingCount,
}: {
  children: ReactNode;
  pendingCount?: number;
}) {
  return (
    <main className="flex min-h-screen grow flex-col bg-page text-body">
      <DashboardTopBar pendingCount={pendingCount} />
      {children}
    </main>
  );
}

/** The frame every admin screen sits in. Two honest sections, not three broken. */
export function AdminPage({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-screen grow flex-col bg-page text-body">
      <DashboardTopBar home="/admin" items={ADMIN_NAV} />
      {children}
    </main>
  );
}
